import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay, isToday } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, XCircle, ShoppingBag,
  ChevronLeft, ChevronRight, Plus
} from "lucide-react";
import { getAppointments, deleteAppointment } from "@/utils/api";
import OutcomeModal from "@/components/OutcomeModal";
import AppointmentScheduler from "@/components/AppointmentScheduler";
// ReminderButton rimosso dalle righe del calendario per ottimizzare lo spazio mobile
import { toast } from "sonner";

const OUTCOME_CONFIG = {
  visited: { label: "Fatto Visita", icon: CheckCircle2, color: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
  not_found: { label: "Non Trovato", icon: XCircle, color: "text-amber-600", badge: "bg-amber-100 text-amber-700" },
  order: { label: "Fatto Ordine", icon: ShoppingBag, color: "text-violet-600", badge: "bg-violet-100 text-violet-700" },
};

export default function CalendarView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initDateStr = searchParams.get("date");

  const [selectedDate, setSelectedDate] = useState(
    initDateStr ? parseISO(initDateStr) : new Date()
  );
  const [month, setMonth] = useState(initDateStr ? parseISO(initDateStr) : new Date());
  const [weekStart, setWeekStart] = useState(
    startOfWeek(initDateStr ? parseISO(initDateStr) : new Date(), { weekStartsOn: 1 })
  );
  const [monthApts, setMonthApts] = useState([]);
  const [dayApts, setDayApts] = useState([]);
  const [weekApts, setWeekApts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outcomeApt, setOutcomeApt] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);

  const loadMonthApts = useCallback(async () => {
    const y = month.getFullYear();
    const m = String(month.getMonth() + 1).padStart(2, "0");
    const start = `${y}-${m}-01`;
    const lastDay = new Date(y, month.getMonth() + 1, 0).getDate();
    const end = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
    try {
      const res = await getAppointments({ start_date: start, end_date: end });
      setMonthApts(res.data);
    } catch {}
  }, [month]);

  const loadDayApts = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await getAppointments({ date: dateStr });
      setDayApts(res.data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const loadWeekApts = useCallback(async () => {
    setLoading(true);
    try {
      const end = endOfWeek(weekStart, { weekStartsOn: 1 });
      const res = await getAppointments({
        start_date: format(weekStart, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
      });
      setWeekApts(res.data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadMonthApts(); }, [loadMonthApts]);
  useEffect(() => { loadDayApts(); }, [loadDayApts]);
  useEffect(() => { loadWeekApts(); }, [loadWeekApts]);

  const appointmentDates = monthApts.map((a) => {
    try { return parseISO(a.scheduled_date); } catch { return null; }
  }).filter(Boolean);

  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { weekStartsOn: 1 }),
  });

  const handleDaySelect = (day) => {
    if (!day) return;
    setSelectedDate(day);
    setWeekStart(startOfWeek(day, { weekStartsOn: 1 }));
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDelete = async (id) => {
    try {
      await deleteAppointment(id);
      toast.success("Appuntamento eliminato");
      setConfirmDeleteId(null);
      loadDayApts();
      loadMonthApts();
      loadWeekApts();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const refresh = () => {
    loadDayApts();
    loadMonthApts();
    loadWeekApts();
  };

  return (
    <div className="p-4 md:p-6 space-y-5 fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A]">Calendario</h1>
        <button
          data-testid="cal-new-apt-btn"
          onClick={() => {
            setScheduleDate(format(selectedDate, "yyyy-MM-dd"));
            setShowScheduler(true);
          }}
          className="flex items-center gap-1.5 bg-[#0F172A] text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors active:scale-95"
        >
          <Plus className="w-4 h-4" />
          Nuovo
        </button>
      </div>

      <div className="overflow-hidden">
        <Tabs defaultValue="monthly" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl w-full max-w-xs">
          <TabsTrigger value="monthly" className="flex-1 rounded-lg text-sm" data-testid="tab-monthly">
            Mensile
          </TabsTrigger>
          <TabsTrigger value="weekly" className="flex-1 rounded-lg text-sm" data-testid="tab-weekly">
            Settimanale
          </TabsTrigger>
        </TabsList>

        {/* Monthly view */}
        <TabsContent value="monthly" className="mt-4">
          <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDaySelect}
                month={month}
                onMonthChange={setMonth}
                modifiers={{ hasAppointment: appointmentDates }}
                modifiersClassNames={{ hasAppointment: "font-bold underline decoration-blue-400" }}
                locale={it}
                weekStartsOn={1}
                className="w-full"
              />
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400 flex gap-3">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                  Ha appuntamenti
                </span>
              </div>
            </div>

            {/* Day detail */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="px-3 py-4 border-b border-slate-100 flex items-center justify-between rounded-t-2xl">
                <div>
                  <h2 className="font-heading font-semibold text-[#0F172A] capitalize">
                    {format(selectedDate, "EEEE d MMMM", { locale: it })}
                  </h2>
                  <p className="text-xs text-slate-400">{dayApts.length} appuntament{dayApts.length === 1 ? "o" : "i"}</p>
                </div>
                <button
                  onClick={() => {
                    setScheduleDate(format(selectedDate, "yyyy-MM-dd"));
                    setShowScheduler(true);
                  }}
                  className="text-xs text-blue-600 font-medium hover:underline flex items-center gap-1"
                  data-testid="add-to-day-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Aggiungi
                </button>
              </div>
              <DayAptList apts={dayApts} loading={loading} onOutcome={setOutcomeApt} onDelete={handleDelete} navigate={navigate} confirmDeleteId={confirmDeleteId} setConfirmDeleteId={setConfirmDeleteId} />
            </div>
          </div>
        </TabsContent>

        {/* Weekly view */}
        <TabsContent value="weekly" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
            {/* Week navigator */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <button
                data-testid="prev-week-btn"
                onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <h2 className="font-heading font-semibold text-[#0F172A] text-sm">
                {format(weekStart, "d MMM", { locale: it })} –{" "}
                {format(endOfWeek(weekStart, { weekStartsOn: 1 }), "d MMM yyyy", { locale: it })}
              </h2>
              <button
                data-testid="next-week-btn"
                onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Week grid */}
            <div className="overflow-x-auto">
              <div className="grid grid-cols-7 min-w-[600px]">
                {weekDays.map((day) => {
                  const dayStr = format(day, "yyyy-MM-dd");
                  const dayAptList = weekApts.filter((a) => a.scheduled_date === dayStr);
                  const todayDay = isToday(day);

                  return (
                    <div
                      key={dayStr}
                      className={`border-r border-slate-100 last:border-r-0 ${todayDay ? "bg-blue-50/50" : ""}`}
                    >
                      <div
                        className={`px-3 py-2 border-b border-slate-100 text-center sticky top-0 ${todayDay ? "bg-blue-50" : "bg-white"}`}
                      >
                        <div className="text-[10px] font-medium text-slate-400 uppercase">
                          {format(day, "EEE", { locale: it })}
                        </div>
                        <div className={`text-sm font-bold mt-0.5 ${todayDay ? "text-blue-600" : "text-[#0F172A]"}`}>
                          {format(day, "d")}
                        </div>
                      </div>

                      <div className="p-1.5 space-y-1 min-h-[120px]">
                        {loading ? (
                          <Skeleton className="h-8 w-full rounded" />
                        ) : (
                          dayAptList.map((apt) => (
                            <WeekAptChip
                              key={apt.id}
                              apt={apt}
                              onOutcome={() => setOutcomeApt(apt)}
                              navigate={navigate}
                            />
                          ))
                        )}
                        <button
                          onClick={() => {
                            setScheduleDate(dayStr);
                            setShowScheduler(true);
                          }}
                          className="w-full text-[10px] text-slate-300 hover:text-blue-400 transition-colors py-1 flex items-center justify-center gap-0.5"
                          data-testid={`add-week-day-${dayStr}`}
                        >
                          <Plus className="w-3 h-3" />
                          Aggiungi
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      </div>

      <OutcomeModal
        appointment={outcomeApt}
        open={!!outcomeApt}
        onClose={() => setOutcomeApt(null)}
        onSuccess={refresh}
      />
      <AppointmentScheduler
        open={showScheduler}
        onClose={() => setShowScheduler(false)}
        onSuccess={refresh}
        preselectedDate={scheduleDate}
      />
    </div>
  );
}

function DayAptList({ apts, loading, onOutcome, onDelete, navigate, confirmDeleteId, setConfirmDeleteId }) {
  if (loading) return (
    <div className="p-5 space-y-3">
      {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
    </div>
  );
  if (apts.length === 0) return (
    <div className="flex flex-col items-center py-12 text-center px-5">
      <CheckCircle2 className="w-8 h-8 text-slate-200 mb-2" />
      <p className="text-sm text-slate-500">Nessun appuntamento per questo giorno</p>
    </div>
  );
  return (
    <div className="divide-y divide-slate-50">
      {apts.map((apt) => {
        const outcome = apt.outcome ? OUTCOME_CONFIG[apt.outcome] : null;
        const Icon = outcome?.icon;
        const isClientApt = !!apt.client_id;
        return (
          <div key={apt.id} data-testid={`day-apt-${apt.id}`} className="flex items-start justify-between px-3 py-3 hover:bg-slate-50 gap-2">
            <div className="flex-1 min-w-0">
              {/* Nome: se ha un cliente → scheda cliente; se occasionale → non navigabile */}
              {isClientApt ? (
                <button
                  onClick={() => navigate(`/app/clienti/${apt.client_id}`)}
                  className="font-semibold text-sm text-[#0F172A] truncate hover:text-blue-600 hover:underline transition-colors text-left w-full"
                  data-testid={`day-apt-client-link-${apt.id}`}
                >
                  {apt.client?.company || apt.client?.name || "—"}
                </button>
              ) : (
                <span
                  className="font-semibold text-sm text-[#0F172A] truncate block"
                  data-testid={`day-apt-title-${apt.id}`}
                >
                  {apt.title || "Appuntamento"}
                </span>
              )}
              {apt.client?.company && apt.client?.name && (
                <div className="text-xs text-slate-400 truncate">{apt.client.name}</div>
              )}
              {apt.client?.last_order_date && (
                <div className="flex items-center gap-0.5 text-[10px] text-violet-500 font-medium mt-0.5">
                  <ShoppingBag className="w-2.5 h-2.5 flex-shrink-0" />
                  Ord. {new Date(apt.client.last_order_date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "2-digit" })}
                </div>
              )}
              {outcome && (
                <div className={`mt-0.5 text-xs font-medium flex items-center gap-1 ${outcome.color}`}>
                  <Icon className="w-3 h-3" />
                  {outcome.label}
                </div>
              )}
              {apt.client?.notes && apt.status === "pending" && (
                <div className="mt-1.5 flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 max-w-xs">
                  <span className="flex-shrink-0">&#128161;</span>
                  <span className="truncate">{apt.client.notes}</span>
                </div>
              )}
              {apt.scheduled_time && (
                <div className="text-xs text-slate-400 mt-0.5">ore {apt.scheduled_time}</div>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {apt.status === "pending" && (
                <button
                  data-testid={`day-esito-${apt.id}`}
                  onClick={() => onOutcome(apt)}
                  className="bg-[#0F172A] text-white text-xs px-2.5 py-1 rounded-lg font-semibold hover:bg-slate-800 active:scale-95 transition-all"
                >
                  Esito
                </button>
              )}
              {confirmDeleteId === apt.id ? (
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`day-delete-confirm-${apt.id}`}
                    onClick={() => onDelete(apt.id)}
                    className="text-xs bg-red-500 text-white px-2 py-1 rounded-lg font-semibold"
                  >
                    Elimina
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="text-xs text-slate-400 px-2 py-1 rounded-lg"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  data-testid={`day-delete-${apt.id}`}
                  onClick={() => setConfirmDeleteId(apt.id)}
                  className="p-1 text-slate-300 hover:text-red-400 transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekAptChip({ apt, onOutcome, navigate }) {
  const outcome = apt.outcome ? OUTCOME_CONFIG[apt.outcome] : null;
  const colorClass = outcome?.badge || "bg-blue-100 text-blue-700";
  const displayName = apt.client?.company || apt.client?.name || "—";

  return (
    <div
      data-testid={`week-apt-${apt.id}`}
      className={`w-full rounded text-[10px] font-medium leading-tight ${colorClass} overflow-hidden`}
    >
      {/* Nome cliccabile → scheda cliente */}
      <button
        onClick={() => apt.client_id && navigate(`/app/clienti/${apt.client_id}`)}
        className="w-full text-left px-1.5 pt-1 truncate hover:opacity-70 transition-opacity block"
        data-testid={`week-apt-client-${apt.id}`}
      >
        {displayName}
      </button>
      {apt.client?.last_order_date && (
        <div className="px-1.5 text-[8px] opacity-70 truncate flex items-center gap-0.5">
          <ShoppingBag className="w-2 h-2 flex-shrink-0" />
          {new Date(apt.client.last_order_date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
        </div>
      )}
      {/* Nota promemoria */}
      {apt.client?.notes && apt.status === "pending" && (
        <div className="px-1.5 pb-1 text-[9px] opacity-75 truncate">&#128161; {apt.client.notes}</div>
      )}
      {/* Bottone esito per appuntamenti pending */}
      {apt.status === "pending" && (
        <button
          onClick={onOutcome}
          className="w-full text-center text-[9px] bg-black/10 hover:bg-black/20 py-0.5 transition-colors"
        >
          Esito
        </button>
      )}
    </div>
  );
}
